import { useLocalSearchParams } from 'expo-router';
import { useResendEmail } from 'features/auth/resetPassword/hooks/useResendEmail/useResendEmail';
import { useSelectChallenge } from 'features/auth/resetPassword/hooks/useSelectChallenge/useSelectChallenge';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import React from 'react';
import { useTranslation } from 'react-i18next';
import EmailVerificationScreen from 'shared/components/EmailVerificationScreen/EmailVerificationScreen';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';

type CheckEmailSearchParams = {
  is1FA: string;
  email: string;
  challengeId: string;
  identityId: string;
};

const CheckEmail = () => {
  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'resetPassword.checkEmail',
  });
  const { is1FA, challengeId, identityId, email } =
    useLocalSearchParams<CheckEmailSearchParams>();
  const allow1FA = is1FA === 'true';
  const { resendEmail } = useResendEmail();
  const { selectChallenge } = useSelectChallenge(email);
  const { segmentClient } = useAnalyticsConfiguration();

  const handleResendCode = async () => {
    if (allow1FA && challengeId) {
      await selectChallenge({
        challengeId,
        identityId,
        isCallForResendCode: true,
      });
    } else {
      const anonymousId = await segmentClient.getAnonymousId();

      await resendEmail({
        anonymousId: anonymousId ?? '',
        emailAddress: email ?? '',
        phoneNumber: '',
      });
    }

    // Return success with no error code to indicate success
    return { errorCode: 0 };
  };

  return (
    <EmailVerificationScreen
      headerText={translatedText('header')}
      titleText={`${translatedText('title')}`}
      openMailButtonLabel={!isWeb ? translatedText('buttons.mail') : undefined}
      newLinkSentText={translatedText('toast')}
      genericErrorText={translatedText('error.generic')}
      description={
        <BodyBaseText>
          {translatedText('description') + email + '.'}
        </BodyBaseText>
      }
      flowKey={
        allow1FA
          ? OtpFlowKey.FORGOT_PASSWORD_1FA
          : OtpFlowKey.FORGOT_PASSWORD_2FA
      }
      onResendLink={handleResendCode}
      onBack={navigateBack}
    />
  );
};

export default CheckEmail;
